import type { BasketDto, HealthLabel, PriceProductDto, PriceUnit } from "@home/shared";
import { useState } from "react";
import { api } from "../api.ts";
import { dayTitle, money } from "../format.ts";
import { t, type Lang } from "../i18n.ts";
import { errorText } from "../meta.ts";
import { hapticSelect, hapticTap } from "../telegram.ts";
import { invalidateRemote, useRemote } from "../useRemote.ts";
import { SkeletonCard } from "./EmptyState.tsx";
import { Segmented } from "./Segmented.tsx";
import { Sheet } from "./Sheet.tsx";
import { toast } from "./Toast.tsx";

/** Сколько товаров видно на карточке; остальные — по кнопке «Все товары». */
const TOP = 5;

export function unitShort(unit: PriceUnit): string {
  return unit === "kg" ? t("unitKg") : unit === "l" ? t("unitL") : t("unitPcs");
}

/** «8 500 сум/кг». */
export function perUnit(price: number, unit: PriceUnit, lang: Lang): string {
  return `${money(price, "UZS", lang)}/${unitShort(unit)}`;
}

/** Карточка «Цены» в отчёте: личная инфляция и цены за единицу на то, что семья покупает чаще всего. */
export function PricesCard({ lang }: { lang: Lang }) {
  const remote = useRemote("prices", () => api.prices());
  const [picked, setPicked] = useState<PriceProductDto | null>(null);
  const [all, setAll] = useState(false);
  const d = remote.data;
  if (!d && remote.loading) return <SkeletonCard lines={3} />;
  if (!d) return null;

  const pick = (p: PriceProductDto) => {
    hapticSelect();
    setPicked(p);
  };

  return (
    <>
      {d.basket && <BasketCard basket={d.basket} />}
      <section className="dash-card">
        <h3>🏷 {t("pricesTitle")}</h3>
        {d.inflation && (
          <div className="inflation">
            <div className={d.inflation.pct > 0 ? "inflation-value bad" : d.inflation.pct < 0 ? "inflation-value good" : "inflation-value"}>
              {d.inflation.pct > 0
                ? `↑ ${t("inflationUp", { p: decimal(d.inflation.pct) })}`
                : d.inflation.pct < 0
                  ? `↓ ${t("inflationDown", { p: decimal(-d.inflation.pct) })}`
                  : t("inflationSame")}
            </div>
            <div className="hint">{t("inflationHint", { n: d.inflation.products })}</div>
          </div>
        )}
        {d.products.length === 0 ? (
          <p className="hint">{t("pricesEmpty")}</p>
        ) : (
          <>
            <PriceRows items={d.products.slice(0, TOP)} lang={lang} onPick={pick} />
            {d.products.length > TOP && (
              <button type="button" className="btn btn-block" onClick={() => setAll(true)}>
                {t("pricesAll", { n: d.products.length })} ›
              </button>
            )}
          </>
        )}
        {all && (
          <Sheet title={`🏷 ${t("pricesTitle")}`} onClose={() => setAll(false)}>
            <PriceRows items={d.products} lang={lang} onPick={pick} />
          </Sheet>
        )}
        {picked && <ProductSheet id={picked.id} name={picked.name} lang={lang} onClose={() => setPicked(null)} />}
      </section>
    </>
  );
}

/** Доля, %: 0,153 → 15. */
function pct(part: number, total: number): number {
  return total > 0 ? Math.round((part / total) * 100) : 0;
}

/**
 * «Что в корзине»: продукты месяца по меткам — три числа, а не график (их три, и главное — одно: доля
 * вредного к обычной). Мягкая подсказка — только если заметно больше или меньше обычного.
 */
function BasketCard({ basket }: { basket: BasketDto }) {
  const total = basket.healthy + basket.neutral + basket.unhealthy;
  const share = basket.unhealthy / total;
  const prev = basket.prevUnhealthyShare;
  const hint = prev === null ? null : share - prev >= 0.05 ? t("basketMore") : prev - share >= 0.05 ? t("basketLess") : null;
  return (
    <section className="dash-card">
      <h3>🧺 {t("basketTitle")}</h3>
      <div className="stat-row three">
        <div className="stat">
          <div className="stat-label">🥦 {t("basketHealthy")}</div>
          <div className="stat-value">{pct(basket.healthy, total)}%</div>
        </div>
        <div className="stat">
          <div className="stat-label">{t("basketNeutral")}</div>
          <div className="stat-value">{pct(basket.neutral, total)}%</div>
        </div>
        <div className="stat">
          <div className="stat-label">🍭 {t("basketUnhealthy")}</div>
          <div className="stat-value">{pct(basket.unhealthy, total)}%</div>
          {prev !== null && <div className="delta muted">{t("basketUsual", { p: Math.round(prev * 100) })}</div>}
        </div>
      </div>
      {hint && <p className="card-text basket-hint">{hint}</p>}
      <p className="hint">{t("basketHint")}</p>
    </section>
  );
}

const HEALTH_OPTIONS: { value: HealthLabel; label: Parameters<typeof t>[0] }[] = [
  { value: "healthy", label: "healthHealthy" },
  { value: "neutral", label: "healthNeutral" },
  { value: "unhealthy", label: "healthUnhealthy" },
];

function PriceRows({ items, lang, onPick }: { items: PriceProductDto[]; lang: Lang; onPick: (p: PriceProductDto) => void }) {
  return (
    <ul className="row-list">
      {items.map((p) => {
        const meta = [
          p.changePct ? (p.changePct > 0 ? t("priceUp", { p: p.changePct }) : t("priceDown", { p: -p.changePct })) : null,
          p.cheapest ? t("cheaperAt", { store: p.cheapest.store ?? t("noStore"), price: perUnit(p.cheapest.price, p.unit, lang) }) : null,
        ].filter(Boolean);
        return (
          <li key={p.id}>
            <button type="button" className="row row-btn" onClick={() => onPick(p)}>
              <div className="row-body">
                <div className="row-title">{p.name}</div>
                {meta.length > 0 && (
                  <div className="row-meta">
                    {p.changePct ? <span className={p.changePct > 0 ? "delta-inline bad" : "delta-inline good"}>{meta[0]}</span> : meta[0]}
                    {meta[1] && ` · ${meta[1]}`}
                  </div>
                )}
              </div>
              <div className="row-end amount">{perUnit(p.lastPrice, p.unit, lang)}</div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Товар подробно: полезное / обычное / вредное (своя метка семьи), как часто покупаете и когда снова,
 * где дешевле и все покупки. Открывается из «Цен» и из холодильника — у товара может не быть цен.
 */
export function ProductSheet({ id, name, lang, onClose }: { id: string; name: string; lang: Lang; onClose: () => void }) {
  const remote = useRemote(`price:${id}`, () => api.priceHistory(id));
  const [saving, setSaving] = useState(false);
  const h = remote.data;

  const setHealth = async (health: HealthLabel) => {
    if (saving || !h || health === h.product.health) return;
    hapticSelect();
    setSaving(true);
    try {
      await api.setProductHealth(id, health);
      hapticTap();
      toast(t("healthSaved"));
      invalidateRemote();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet title={name} onClose={onClose}>
      {!h ? (
        <div className="skeleton skeleton-block" />
      ) : (
        <>
          <div className="field">
            <span>{t("healthTitle")}</span>
            <Segmented
              value={h.product.health}
              onChange={(v) => void setHealth(v)}
              label={t("healthTitle")}
              options={HEALTH_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))}
            />
          </div>
          {h.rhythm && (
            <p className="card-text rhythm-text">
              {t("rhythmEvery", { n: h.rhythm.everyDays })}: {t("rhythmLast", { day: dayTitle(h.rhythm.lastDay, lang).toLowerCase() })},{" "}
              {h.rhythm.dueIn > 0 ? t("rhythmNext", { n: h.rhythm.dueIn }) : t("dueNow")}.
            </p>
          )}
          {h.points.length === 0 ? (
            <p className="hint">{t("noPricesYet")}</p>
          ) : (
            <>
              <p className="hint">{t("perUnit", { unit: unitShort(h.product.unit) })}</p>
              {h.stores.length >= 2 && (
                <>
                  <div className="section-title">{t("whereCheaper")}</div>
                  <ul className="plain-list">
                    {h.stores.map((s, i) => (
                      <li key={s.store ?? ""}>
                        <span>
                          {i === 0 ? "✅ " : ""}
                          {s.store ?? t("noStore")}
                        </span>
                        <b>{perUnit(s.price, h.product.unit, lang)}</b>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <div className="section-title">{t("purchasesTitle")}</div>
              <ul className="row-list">
                {h.points.map((p, i) => (
                  <li key={i} className="row">
                    <div className="row-body">
                      <div className="row-title">{dayTitle(p.date, lang)}</div>
                      <div className="row-meta">
                        {p.store ?? t("noStore")} ·{" "}
                        {t("boughtQty", { qty: formatQty(p.qty), unit: unitShort(h.product.unit), amount: money(p.amount, "UZS", lang) })}
                      </div>
                    </div>
                    <div className="row-end amount">{perUnit(p.price, h.product.unit, lang)}</div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </Sheet>
  );
}

function formatQty(qty: number): string {
  return decimal(Math.round(qty * 1000) / 1000);
}

/** Десятичная запятая, как принято в русском и узбекском: «2,4». */
function decimal(n: number): string {
  return String(n).replace(".", ",");
}
